import CatalogCategories from "@/pages/configuration/delay-categories";
import { BarrelsPage } from "@/pages/configuration/barrels";
import { PassTypesPage } from "@/pages/configuration/pass-types";
import { PassIdsPage } from "@/pages/configuration/pass-ids";
import { CartridgesPage } from "@/pages/configuration/cartridges";
import { PassChangeReasonsPage } from "@/pages/configuration/pass-change-reasons";
import { RollMaterialsPage } from "@/pages/configuration/roll-materials";
import { RollApplicationsPage } from "@/pages/configuration/roll-applications";
import { RollsBlankPage } from "@/pages/configuration/rolls-blank";
import { CostCentersPage } from "@/pages/configuration/cost-centers";
import { OperatorsPage } from "@/pages/configuration/operators";
import { SuppliersPage } from "@/pages/configuration/suppliers";
import { ProductTypesPage } from "@/pages/configuration/product-types";
import { ProductsPage } from "@/pages/configuration/products";
import { ProductChangesPage } from "@/pages/configuration/product-changes";
import { RecipesPage } from "@/pages/configuration/recipes";
import { StraightenerLimitsPage } from "@/pages/configuration/straightener-limits";
import {
	PERMISSION_STRAIGHTENER_LIMITS,
	PERMISSION_STRAIGHTENER_RECIPES,
} from "@/pages/production/straightener-common/permissions";
import { StraightenerRecipesPage } from "@/pages/production/straightener-recipes";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BadgeIcon from "@mui/icons-material/Badge";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CategoryIcon from "@mui/icons-material/Category";
import SettingsIcon from "@mui/icons-material/Settings";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TimelineIcon from "@mui/icons-material/Timeline";
import StraightenIcon from "@mui/icons-material/Straighten";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import ScienceIcon from "@mui/icons-material/Science";
import AppsIcon from "@mui/icons-material/Apps";
import CircleIcon from "@mui/icons-material/Circle";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import InventoryIcon from "@mui/icons-material/Inventory";
import LabelIcon from "@mui/icons-material/Label";
import {
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	ListSubheader,
	useTheme,
} from "@mui/material";
import { useHasPermission } from "@dexteel/mesf-core";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import CatalogDelayCodes from "../pages/configuration/delay-codes";

type Component = {
	path: string;
	sidebar: () => ReactNode;
	main: () => ReactNode;
};

const components: Component[] = [
	// R-Factor components
	{
		path: "barrels",
		sidebar: () => <div>Barrels</div>,
		main: () => <BarrelsPage />,
	},
	{
		path: "pass-types",
		sidebar: () => <div>Pass Types</div>,
		main: () => <PassTypesPage />,
	},
	{
		path: "pass-ids",
		sidebar: () => <div>Pass IDs</div>,
		main: () => <PassIdsPage />,
	},
	// Delays components
	{
		path: "delay-categories",
		sidebar: () => <div>Categories</div>,
		main: () => <CatalogCategories />,
	},
	{
		path: "delay-codes",
		sidebar: () => <div>Codes</div>,
		main: () => <CatalogDelayCodes delayAreaAssetName="RBD Mill" />,
	},
	{
		path: "pass-change-reasons",
		sidebar: () => <div>Pass Change Reasons</div>,
		main: () => <PassChangeReasonsPage />,
	},
	{
		path: "cartridges",
		sidebar: () => <div>Cartridges</div>,
		main: () => <CartridgesPage />,
	},
	{
		path: "roll-materials",
		sidebar: () => <div>Roll Materials</div>,
		main: () => <RollMaterialsPage />,
	},
	{
		path: "roll-applications",
		sidebar: () => <div>Roll Applications</div>,
		main: () => <RollApplicationsPage />,
	},
	{
		path: "rolls-blank",
		sidebar: () => <div>Rolls Blank</div>,
		main: () => <RollsBlankPage />,
	},
	{
		path: "cost-centers",
		sidebar: () => <div>Cost Centers</div>,
		main: () => <CostCentersPage />,
	},
	{
		path: "operators",
		sidebar: () => <div>Operators</div>,
		main: () => <OperatorsPage />,
	},
	{
		path: "suppliers",
		sidebar: () => <div>Suppliers</div>,
		main: () => <SuppliersPage />,
	},
	{
		path: "product-types",
		sidebar: () => <div>Product Types</div>,
		main: () => <ProductTypesPage />,
	},
	{
		path: "products",
		sidebar: () => <div>Products</div>,
		main: () => <ProductsPage />,
	},
	{
		path: "product-changes",
		sidebar: () => <div>Product Changes</div>,
		main: () => <ProductChangesPage />,
	},
	{
		path: "recipes",
		sidebar: () => <div>Recipes</div>,
		main: () => <RecipesPage />,
	},
	{
		path: "straightener-limits",
		sidebar: () => <div>Parameters</div>,
		main: () => <StraightenerLimitsPage />,
	},
	{
		path: "straightener-recipes",
		sidebar: () => <div>Recipes</div>,
		main: () => <StraightenerRecipesPage />,
	},
];

const sidebar = ({ option }: { option?: string }) => {
	const theme = useTheme();
	const hasPermissionTo = useHasPermission();
	const canOpenStraightenerLimits = hasPermissionTo(
		PERMISSION_STRAIGHTENER_LIMITS,
	);
	const canOpenStraightenerRecipes = hasPermissionTo(
		PERMISSION_STRAIGHTENER_RECIPES,
	);

	const subheaderStyle = {
		backgroundColor: theme.palette.background.paper,
		color: theme.palette.primary.main,
		fontWeight: 600,
	};

	return (
		<>
			{/* Mill Section */}
			<List
				subheader={<ListSubheader style={subheaderStyle}>Mill</ListSubheader>}
			>
				<ListItemButton
					selected={option === "barrels"}
					component={Link}
					to="/configuration/barrels"
				>
					<ListItemIcon>
						<StraightenIcon />
					</ListItemIcon>
					<ListItemText primary="Barrels" />
				</ListItemButton>
				<ListItemButton
					selected={option === "cartridges"}
					component={Link}
					to="/configuration/cartridges"
				>
					<ListItemIcon>
						<ViewModuleIcon />
					</ListItemIcon>
					<ListItemText primary="Cartridges" />
				</ListItemButton>
				<ListItemButton
					selected={option === "operators"}
					component={Link}
					to="/configuration/operators"
				>
					<ListItemIcon>
						<BadgeIcon />
					</ListItemIcon>
					<ListItemText primary="Operators" />
				</ListItemButton>
				<ListItemButton
					selected={option === "pass-change-reasons"}
					component={Link}
					to="/configuration/pass-change-reasons"
				>
					<ListItemIcon>
						<SwapHorizIcon />
					</ListItemIcon>
					<ListItemText primary="Pass Change Reasons" />
				</ListItemButton>
				<ListItemButton
					selected={option === "pass-ids"}
					component={Link}
					to="/configuration/pass-ids"
				>
					<ListItemIcon>
						<FormatListNumberedIcon />
					</ListItemIcon>
					<ListItemText primary="Pass IDs" />
				</ListItemButton>
				<ListItemButton
					selected={option === "pass-types"}
					component={Link}
					to="/configuration/pass-types"
				>
					<ListItemIcon>
						<SettingsIcon />
					</ListItemIcon>
					<ListItemText primary="Pass Types" />
				</ListItemButton>
				<ListItemButton
					selected={option === "product-changes"}
					component={Link}
					to="/configuration/product-changes"
				>
					<ListItemIcon>
						<CompareArrowsIcon />
					</ListItemIcon>
					<ListItemText primary="Product Changes" />
				</ListItemButton>
				<ListItemButton
					selected={option === "product-types"}
					component={Link}
					to="/configuration/product-types"
				>
					<ListItemIcon>
						<LabelIcon />
					</ListItemIcon>
					<ListItemText primary="Product Types" />
				</ListItemButton>
				<ListItemButton
					selected={option === "products"}
					component={Link}
					to="/configuration/products"
				>
					<ListItemIcon>
						<InventoryIcon />
					</ListItemIcon>
					<ListItemText primary="Products" />
				</ListItemButton>
				<ListItemButton
					selected={option === "recipes"}
					component={Link}
					to="/configuration/recipes"
				>
					<ListItemIcon>
						<MenuBookIcon />
					</ListItemIcon>
					<ListItemText primary="Recipes" />
				</ListItemButton>
			</List>

			{/* Straightener Section: shown when the user can open either page */}
			{canOpenStraightenerLimits || canOpenStraightenerRecipes ? (
				<List
					subheader={
						<ListSubheader style={subheaderStyle}>Straightener</ListSubheader>
					}
				>
					{canOpenStraightenerLimits ? (
						<ListItemButton
							selected={option === "straightener-limits"}
							component={Link}
							to="/configuration/straightener-limits"
						>
							<ListItemIcon>
								<TimelineIcon />
							</ListItemIcon>
							<ListItemText primary="Parameters" />
						</ListItemButton>
					) : null}
					{canOpenStraightenerRecipes ? (
						<ListItemButton
							selected={option === "straightener-recipes"}
							component={Link}
							to="/configuration/straightener-recipes"
						>
							<ListItemIcon>
								<MenuBookIcon />
							</ListItemIcon>
							<ListItemText primary="Recipes" />
						</ListItemButton>
					) : null}
				</List>
			) : null}

			{/* Rollshop Section */}
			<List
				subheader={
					<ListSubheader style={subheaderStyle}>Rollshop</ListSubheader>
				}
			>
				<ListItemButton
					selected={option === "cost-centers"}
					component={Link}
					to="/configuration/cost-centers"
				>
					<ListItemIcon>
						<AccountBalanceIcon />
					</ListItemIcon>
					<ListItemText primary="Cost Centers" />
				</ListItemButton>
				<ListItemButton
					selected={option === "roll-applications"}
					component={Link}
					to="/configuration/roll-applications"
				>
					<ListItemIcon>
						<AppsIcon />
					</ListItemIcon>
					<ListItemText primary="Roll Applications" />
				</ListItemButton>
				<ListItemButton
					selected={option === "roll-materials"}
					component={Link}
					to="/configuration/roll-materials"
				>
					<ListItemIcon>
						<ScienceIcon />
					</ListItemIcon>
					<ListItemText primary="Roll Materials" />
				</ListItemButton>
				<ListItemButton
					selected={option === "rolls-blank"}
					component={Link}
					to="/configuration/rolls-blank"
				>
					<ListItemIcon>
						<CircleIcon />
					</ListItemIcon>
					<ListItemText primary="Rolls Blank" />
				</ListItemButton>
				<ListItemButton
					selected={option === "suppliers"}
					component={Link}
					to="/configuration/suppliers"
				>
					<ListItemIcon>
						<LocalShippingIcon />
					</ListItemIcon>
					<ListItemText primary="Suppliers" />
				</ListItemButton>
			</List>

			{/* Stops Section */}
			<List
				subheader={<ListSubheader style={subheaderStyle}>Stops</ListSubheader>}
			>
				<ListItemButton
					selected={option === "categories"}
					component={Link}
					to="/configuration/delay-categories"
				>
					<ListItemIcon>
						<CategoryIcon />
					</ListItemIcon>
					<ListItemText primary="Categories" />
				</ListItemButton>
				<ListItemButton
					selected={option === "codes"}
					component={Link}
					to="/configuration/delay-codes"
				>
					<ListItemIcon>
						<TimelineIcon />
					</ListItemIcon>
					<ListItemText primary="Codes" />
				</ListItemButton>
			</List>
		</>
	);
};

const customConfigurations: any = [components, sidebar];

export { customConfigurations };
